import { useCallback, useContext, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';

import { makeApiRequest } from '../../../Shared/Api';
import { applyBidRealtimeToJobDetail } from '../../../Shared/bidRealtime';
import { useRealtimeEvent } from '../../../Context/RealtimeContext';
import { JobDetailContext } from '../context/JobDetailContext';

export const useJobDetail = (jobId) => {
	const navigate = useNavigate();
	const { enqueueSnackbar } = useSnackbar();
	const { state, dispatch } = useContext(JobDetailContext);
	const { selectedJobId, selectedJob, loading, error } = state;
	const selectedJobRef = useRef(selectedJob);
	selectedJobRef.current = selectedJob;

	const fetchJob = useCallback(async () => {
		if (!jobId) {
			return;
		}
		dispatch({ type: 'SET_LOADING', payload: true });
		try {
			const response = await makeApiRequest('GET', `/jobs/${jobId}/`);
			dispatch({ type: 'SET_JOB', payload: response });
			if (!selectedJobId) {
				dispatch({ type: 'SET_SELECTED_JOB_ID', payload: jobId });
			}
		} catch (err) {
			console.error('Error fetching job details:', err);
			dispatch({ type: 'SET_ERROR', payload: err.message });
		} finally {
			dispatch({ type: 'SET_LOADING', payload: false });
		}
	}, [dispatch, jobId, selectedJobId]);

	useEffect(() => {
		if (!jobId) {
			navigate('/jobs');
			return;
		}
		fetchJob();
	}, [fetchJob, jobId, navigate]);

	const handleRealtimeUpdate = useCallback(
		(message) => {
			const currentJob = selectedJobRef.current;
			if (!currentJob) {
				return;
			}
			const updated = applyBidRealtimeToJobDetail(currentJob, message);
			if (updated !== currentJob) {
				dispatch({ type: 'SET_JOB', payload: updated });
			}
		},
		[dispatch],
	);

	useRealtimeEvent(handleRealtimeUpdate);

	const patchJobField = useCallback(
		async (value, key) => {
			try {
				const response = await makeApiRequest('PATCH', `/jobs/${jobId}/`, {
					[key]: value,
				});
				dispatch({ type: 'SET_JOB', payload: response });
				return response;
			} catch (err) {
				enqueueSnackbar(
					err.message || 'Failed to update job. Please try again.',
					{ variant: 'error' },
				);
				throw err;
			}
		},
		[dispatch, enqueueSnackbar, jobId],
	);

	const patchJobFields = useCallback(
		async (fields) => {
			try {
				const response = await makeApiRequest('PATCH', `/jobs/${jobId}/`, fields);
				dispatch({ type: 'SET_JOB', payload: response });
				return response;
			} catch (err) {
				enqueueSnackbar(
					err.message || 'Failed to update job. Please try again.',
					{ variant: 'error' },
				);
				throw err;
			}
		},
		[dispatch, enqueueSnackbar, jobId],
	);

	const updateJobLocal = useCallback(
		(updates) => {
			if (!selectedJob) {
				return;
			}
			dispatch({
				type: 'SET_JOB',
				payload:
					typeof updates === 'function'
						? updates(selectedJob)
						: { ...selectedJob, ...updates },
			});
		},
		[dispatch, selectedJob],
	);

	const clearJob = useCallback(() => {
		dispatch({ type: 'SET_SELECTED_JOB_ID', payload: null });
		dispatch({ type: 'SET_JOB', payload: null });
	}, [dispatch]);

	return {
		job: selectedJob,
		loading,
		error,
		fetchJob,
		patchJobField,
		patchJobFields,
		updateJobLocal,
		clearJob,
	};
};

export default useJobDetail;
